import React from 'react';

type UseSingleLineVisibleParams = {
  items?: string[];
  gap?: number;
};

type UseSingleLineVisibleResult = {
  wrapRef: React.RefObject<HTMLDivElement>;
  measureRef: React.RefObject<HTMLSpanElement>;
  visibleCount: number;
};

export const useSingleLineVisible = ({
  items,
  gap = 16,
}: UseSingleLineVisibleParams): UseSingleLineVisibleResult => {
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const measureRef = React.useRef<HTMLSpanElement>(null);
  const [visibleCount, setVisibleCount] = React.useState<number>(0);

  React.useEffect(() => {
    if (!items || items.length === 0) {
      setVisibleCount(0);
      return;
    }

    const calcWidth = () => {
      const wrapEl = wrapRef.current;
      const measureEl = measureRef.current;

      if (!wrapEl || !measureEl) {
        setVisibleCount(items.length);
        return;
      }

      const wrapWidth = wrapEl.clientWidth;
      if (!wrapWidth) {
        setVisibleCount(items.length);
        return;
      }

      let usedWidth = 0;
      let count = 0;

      for (const item of items) {
        measureEl.textContent = item;
        const itemWidth = measureEl.offsetWidth;
        const needWidth = count > 0 ? itemWidth + gap : itemWidth;

        if (usedWidth + needWidth <= wrapWidth) {
          usedWidth += needWidth;
          count += 1;
        } else {
          break;
        }
      }

      setVisibleCount(count);
    };

    const rafId = window.requestAnimationFrame(calcWidth);
    return () => window.cancelAnimationFrame(rafId);
  }, [items, gap]);

  return {
    wrapRef,
    measureRef,
    visibleCount,
  };
};
